import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import { TradingOffer } from 'types';
import {
  PartnerAgreedTradingOffer,
} from '../../../../../types/TradingOffer';
import { TradingOffersListContextType } from '../types/TradingOffersListContextType';
import { tradingOffersListInitValue } from '../constants/TradingOffersListInitValue';
import { TradingOfferSortOption } from '../constants/tradingOffersSortOptions';
import {
  TRADING_OFFERS_PAGE_SIZE,
  TRADING_OFFERS_FILTERS,
} from '../../../constants/tradingOffersConfig';
import { useGetTradingOffers } from '../../../hooks/useGetTradingOffers';
import calculateOffersSummary from '../../../utils/calculateOffersSummary';
import { filterSellOffers, filterBuyOffers } from '../../../utils/filterOffersByType';

export const TradingOffersListContext =
  createContext<TradingOffersListContextType>(tradingOffersListInitValue);

export const TradingOffersListProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const { societyId, userId } = useParams<{
    societyId: string;
    userId: string;
  }>();
  const partnerQuery = new URLSearchParams(location.search).get('partnerId');
  const partnerQueryParam = partnerQuery ? `?partnerId=${partnerQuery}` : '';

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const tradingOffers = useSelector(
    ({ tradingOffers }: State) => (tradingOffers ?? []) as TradingOffer[]
  );

  const partnerOperations = useMemo<PartnerAgreedTradingOffer[]>(() => [], []);
  const [sellOffersSortBy, setSellOffersSortBy] =
    useState<TradingOfferSortOption>('price');
  const [buyOffersSortBy, setBuyOffersSortBy] =
    useState<TradingOfferSortOption>('price');

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { isLoading: isTradingOffersLoading } = useGetTradingOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  const isReadOnly = false;

  const sellOffers = useMemo(
    () => filterSellOffers(tradingOffers),
    [tradingOffers]
  );

  const buyOffers = useMemo(
    () => filterBuyOffers(tradingOffers),
    [tradingOffers]
  );

  const sellOffersSummary = useMemo(
    () => calculateOffersSummary(sellOffers),
    [sellOffers]
  );

  const buyOffersSummary = useMemo(
    () => calculateOffersSummary(buyOffers),
    [buyOffers]
  );

  const activeOperationsCount = useMemo(
    () => partnerOperations.filter((op) => !op.isApproved).length,
    [partnerOperations]
  );

  const totalOperationsCount = useMemo(
    () => partnerOperations.length,
    [partnerOperations]
  );

  const sortOffers = useCallback(
    (offers: TradingOffer[], sortBy: TradingOfferSortOption): TradingOffer[] =>
      [...offers].sort((a, b) => {
        switch (sortBy) {
          case 'price':
            return b.sharePrice - a.sharePrice;
          case 'date':
            return (
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
            );
          case 'shareClass':
            return String(a.shareClass).localeCompare(String(b.shareClass));
          default:
            return 0;
        }
      }),
    []
  );

  const sortedSellOffers = useMemo(
    () => sortOffers(sellOffers, sellOffersSortBy),
    [sellOffers, sellOffersSortBy, sortOffers]
  );

  const sortedBuyOffers = useMemo(
    () => sortOffers(buyOffers, buyOffersSortBy),
    [buyOffers, buyOffersSortBy, sortOffers]
  );

  const handleCreateSellOffer = useCallback(() => {
    history.push(`/crear-oferta-venta/${societyId}${partnerQueryParam}`);
  }, [history, societyId, partnerQueryParam]);

  const handleCreateBuyOffer = useCallback(() => {
    history.push(`/crear-oferta-compra/${societyId}${partnerQueryParam}`);
  }, [history, societyId, partnerQueryParam]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta/${societyId}/${offerId}${partnerQueryParam}`);
    },
    [history, societyId, partnerQueryParam]
  );

  const handleSellOffersSortChange = useCallback(
    (sortBy: TradingOfferSortOption) => {
      setSellOffersSortBy(sortBy);
    },
    []
  );

  const handleBuyOffersSortChange = useCallback(
    (sortBy: TradingOfferSortOption) => {
      setBuyOffersSortBy(sortBy);
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        sellOffers: sortedSellOffers,
        buyOffers: sortedBuyOffers,
        partnerOperations,
        sellOffersSummary,
        buyOffersSummary,
        activeOperationsCount,
        totalOperationsCount,
        sellOffersSortBy,
        buyOffersSortBy,
        isAdmin,
        isReadOnly,
        isLoading: isTradingOffersLoading || !actualSociety,
      },
      actions: {
        handleCreateSellOffer,
        handleCreateBuyOffer,
        handleViewOfferDetail,
        handleSellOffersSortChange,
        handleBuyOffersSortChange,
      },
    }),
    [
      societyId,
      actualSociety,
      sortedSellOffers,
      sortedBuyOffers,
      partnerOperations,
      sellOffersSummary,
      buyOffersSummary,
      activeOperationsCount,
      totalOperationsCount,
      sellOffersSortBy,
      buyOffersSortBy,
      isAdmin,
      isReadOnly,
      isTradingOffersLoading,
      handleCreateSellOffer,
      handleCreateBuyOffer,
      handleViewOfferDetail,
      handleSellOffersSortChange,
      handleBuyOffersSortChange,
    ]
  );

  return (
    <TradingOffersListContext.Provider value={contextValue}>
      {children}
    </TradingOffersListContext.Provider>
  );
};

export const useTradingOffersList = () => {
  const context = useContext(TradingOffersListContext);
  if (!context) {
    throw new Error(
      'useTradingOffersList must be used within a TradingOffersListProvider'
    );
  }
  return context;
};
